import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { Component, OnInit } from '@angular/core';
import { debounce } from 'lodash';
import { ExportType, ReportType, SubscriptionManagementApiModel, SubscriptionRowNode } from 'src/app/admin/Subscription/subscription-management-model';
import { SubscriptionService } from 'src/app/admin/Subscription/subscription.service';
import { DashboardService } from 'src/app/dashboard/dashboard.service';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { DashboardResponsiveApiModelSparse } from 'src/shared/models/dashboard.model';
import { ToastrManager } from '@niceltd/cxone-components/toastr';

@Component({
    selector: 'cv-subscription-management',
    templateUrl: './subscription-management.component.html',
    styleUrls: ['./subscription-management.component.scss']
})
export class SubscriptionManagementComponent implements OnInit {
    public filterValue: string = "";
    public tab: number = 0;
    public gridOptions: GridOptions = {
        rowData: [],
        columnDefs: [],
        defaultColDef: {
            cellClass: "d-flex align-items-center",
            filter: "agTextColumnFilter",
            suppressMenu: true
        },
        enableBrowserTooltips: true,
        rowSelection: 'multiple',
        rowMultiSelectWithClick: true,
        suppressCellFocus: true,
        suppressContextMenu: true,
        suppressDragLeaveHidesColumns: true,
        suppressLoadingOverlay: true,
        suppressNoRowsOverlay: true,
        suppressHorizontalScroll: true,
        rowHeight: 42,
        onGridReady: (params) => {
            this._gridApi = params.api;
            params.api.sizeColumnsToFit();
        },
        getRowId: (params) => params.data.ID,
        isExternalFilterPresent: () => true,
        doesExternalFilterPass: (node) => this._gridHelperService.doesExternalFilterPass(node, this.filterValue) && node.data.Legacy == this.tab,
        onColumnVisible: (event) => event.api.sizeColumnsToFit(),
        onGridSizeChanged: (event) => event.api.sizeColumnsToFit(),
        onRowSelected: (event) => this.selectedRows = event.api.getSelectedRows().length,
    };
    public selectedRows: number = 0;
    public legacyCount: number = 0;
    public legacyPermissions: boolean = false;
    public loading: boolean = true;

    // Widget subscription editor
    public selected: SubscriptionRowNode = null;
    public editMode: boolean = false;
    public showNewWidget: boolean = false;

    // Dashboard selector for new dashbaord subscription
    public showNewDashboard: boolean = false;
    public loadingDashboards: boolean = false;
    public dashboards: DashboardResponsiveApiModelSparse[];
    public selectedDashboard: DashboardResponsiveApiModelSparse = null;

    // Subscription Scheduler for new dashboard and edit subscription
    public showScheduler: boolean = false;
    public reportType: ReportType = ReportType.Dashboard;
    public subscriptionId: number = -1;
    public title: string = "";
    public relatedId: number = null;
    public relatedKey: string = null;

    // Subscription Editor for edit legacy subscription
    public showLegacyEdit: boolean = false;
    public legacySubscription: SubscriptionRowNode;

    private _gridApi: GridApi;
    private _gridData: SubscriptionRowNode[] = [];
    private orderBy: string = "";
    private _localization: {
        subscriptionManagement: Map<string, string>,
        common: Map<string, string>
    } = {
            subscriptionManagement: undefined,
            common: undefined
        };
    private refreshTimer: NodeJS.Timeout = null;
    private refreshTime: number = 600000; // 10 minutes

    constructor(
        private _subscriptionService: SubscriptionService,
        private _dashboardService: DashboardService,
        private _localizationService: CVLocalizationService,
        private _gridHelperService: AgGridHelperService,
        private _toastrManager: ToastrManager,
    ) { }

    async ngOnInit() {
        this._localization.subscriptionManagement = await this._localizationService.GetSection("Subscriptions_Management");
        this._localization.common = await this._localizationService.GetSection("Common");
        this._gridApi?.setGridOption("columnDefs", this.getColumnDefs());
        this._gridApi?.sizeColumnsToFit();
        await this.loadData();
    }

    public changeTab(tab: number) {
        this.tab = tab;
        this._gridApi?.deselectAll();
        this.filterGrid();
    }

    public applyFilterAgGrid = debounce(this.filterGrid, 400);

    public async delete() {
        this.loading = true;
        let selectedRows: SubscriptionRowNode[] = this._gridApi?.getSelectedRows();
        let deletePromises: Promise<boolean>[] = [];
        selectedRows.forEach((subscription) => {
            deletePromises.push(this._subscriptionService.deleteSubscription(subscription.ID, (this.tab == 0) ? 0 : subscription.Type + 1)
                .then((response) => response).catch((reason) => false));
        });

        let results = await Promise.all(deletePromises);
        let deleted = 0;
        results.forEach((r) => { 
            if (r){
                ++deleted;
                this._toastrManager.showToaster('success',
                    this._localizationService.getCachedText("Subscriptions_Management", "Subscription_Deleted"));
            }
        });

        if (deleted > 0) {
            if (this.tab == 1 && selectedRows.length == this.legacyCount && this.legacyCount == deleted)
                this.changeTab(0);

            this.loadData();
            this.selectedRows -= deleted;
        }
    }

    public edit() {
        let selected: SubscriptionRowNode = this._gridApi?.getSelectedRows()[0];
        if (this.tab == 0) {
            this.reportType = selected.Type;
            this.subscriptionId = selected.ID;
            this.relatedId = selected.RelatedID;
            this.relatedKey = selected.RelatedKey;
            this.title = selected.Name

            if (this.reportType == ReportType.Dashboard)
                this.showScheduler = true;
            else {
                this.showScheduler = true;
                /* Remove preceeding line and uncomment these lines to edit Widget Subscription using same component as they are 
                    created with */
                //this.editMode = true;
                //this.selected = selected;
                //this.showNewWidget = true;
            }
        }
        else {
            this.legacySubscription = selected;
            this.showLegacyEdit = true;
        }
    }

    public exportBulk() {
        const columnState = this._gridApi?.getColumnState();
        const sortedColumn = columnState.filter(s => s.sort != null);

        if(sortedColumn && sortedColumn.length > 0)
            this.orderBy = (sortedColumn[0].sort === 'asc' ? '' : '-') + sortedColumn[0].colId;
        else
            this.orderBy = "";

        this._subscriptionService.exportBulk(this.filterValue, (this.tab === 0) ? false : true, this.orderBy);
    }

    public async exportIndividual() {
        let selected: SubscriptionRowNode = this._gridApi?.getSelectedRows()[0];
        let data = await this._subscriptionService.exportSubscription(selected.ID, (this.tab == 0) ? 0 : selected.Type + 1);
        if (!!data) {
            let decoded = atob(data);
            let buffer = new ArrayBuffer(decoded.length);
            let view = new Uint8Array(buffer);
            for (let i = 0; i != decoded.length; ++i)
                view[i] = decoded.charCodeAt(i) & 0xFF;
            let fileData = [buffer];
            let mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            if (selected.ExportType == ExportType.CSV)
                mimeType = "text/csv";
            let blob = new Blob(fileData, { type: mimeType });

            let link = document.createElement('a');
            link.setAttribute('style', 'display:none');
            let url = URL.createObjectURL(blob);
            link.href = url;
            link.download = selected.Name;

            document.body.appendChild(link);
            link.click();
            URL.revokeObjectURL(link.href);
            link.remove();
        }
    }

    public onSaved() {
        this.loading = true;
        this.onClosed();
        this.loadData();
    }

    public onClosed() {
        this.showNewWidget = false;
        this.showScheduler = false;
        this.showLegacyEdit = false;
    }

    public openNewWidget() {
        this.editMode = false;
        this.selected = null;
        this.showNewWidget = true;
    }

    public async openNewDashboardSubscription() {
        this.loadingDashboards = this.showNewDashboard = true;
        let response = await this._dashboardService.loadExistingDashboardsAndGroups();
        if (response.status_code != 200) {
            console.error("Failed getting dashboards");
            return;
        }
        this.dashboards = response.data.Dashboards;
        this.loadingDashboards = false;
    }

    public newDashboardOnNext() {
        this.showNewDashboard = false;

        this.reportType = 0;
        this.subscriptionId = -1;
        this.title = this.selectedDashboard.DashboardName;
        this.relatedId = this.selectedDashboard.DashboardID;
        this.relatedKey = this.selectedDashboard.DashboardKey;

        this.selectedDashboard = null;

        this.showScheduler = true;
    }

    private filterGrid() {
        this._gridApi?.onFilterChanged();
    }

    private getColumnDefs(): ColDef[] {
        return [
            {
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
                checkboxSelection: true,
                sortable: false,
                width: 40,
                suppressSizeToFit: true,
            },
            {
                field: "Name",
                colId: "Name",
                headerName: this._localization.common.get('Name') ?? "Name",
            },
            {
                field: "TypeText",
                colId: "Type",
                headerName: this._localization.subscriptionManagement.get('Type') ?? "Type",
                width: 98,
                suppressSizeToFit: true,
            },
            {
                field: "Creator",
                colId: "Creator",
                headerName: this._localization.subscriptionManagement.get('Creator') ?? "Creator",
            },
            {
                field: "FrequencyText",
                colId: "Frequency",
                headerName: this._localization.subscriptionManagement.get('Frequency') ?? "Frequency",
                width: 98,
                suppressSizeToFit: true,
            },
            {
                field: "TimeZone",
                colId: "TimeZone",
                headerName: this._localization.subscriptionManagement.get('Time_Zone') ?? "Time Zone",
                suppressSizeToFit: true,
                cellRenderer: (params) => {
                    if (params.node.data.TimeZoneDefault)
                        return "<div class='timeZoneTooltip'>" +
                            "<div class='bg-white d-none px-1 border border-secondary position-absolute' style='line-height:18px; top:16px; left:72px; pointer-events:none'>" +
                            this._localization.subscriptionManagement.get('Company_Default') +
                            "</div><span>" + params.value + "</span></div>";
                    else
                        return "<span>" + params.value + "</span>";
                },
            },
            {
                field: "Recipients",
                colId: "Recipients",
                headerName: this._localization.subscriptionManagement.get('Recipients') ?? "Recipients",
                cellRenderer: (params) => {
                    return "<span class='color-whaleTail'>(" + params.value.length + ")</span> " + params.value.join(', ');
                },
            },
            {
                field: "UnsubscribedRecipients",
                colId: "UnsubscribedRecipients",
                headerName: this._localization.subscriptionManagement.get('Unsubscribed'),
                cellRenderer: (params) => {
                    const value = params?.value[0] == null ? "" : params?.value[0];
                    return "<span class='color-whaleTail'>(" + params?.value.length + ")</span> " + value;
                },
                tooltipValueGetter: (params) => {
                    let unsubscribedString = "";

                    if (params?.value.length > 1)
                        for(let i = 1; i < params?.value.length; i++)
                            unsubscribedString += params.value[i] + "\n";

                    return unsubscribedString;
                },
            }
        ];
    }

    private async loadData() {
        this.loading = true;
        const data: SubscriptionManagementApiModel = await this._subscriptionService.getSubscriptions();
        this.legacyPermissions = data.LegacyPermissions;
        this._gridData = data.Subscriptions;
        this.legacyCount = data.Subscriptions.reduce((n, subscription) => n + ((subscription.Legacy === true) ? 1 : 0), 0);

        // Set grid data and resize
        this._gridApi?.setGridOption("rowData", this._gridData);
        this._gridApi?.sizeColumnsToFit();
        this.loading = false;
        this.startInterval();
    }

    private startInterval(){
        this.stopInterval();
        this.refreshTimer = setTimeout(() => {
            this.loadData();
        }, this.refreshTime);
    }

    private stopInterval(){
        if(this.refreshTimer != null)
            clearTimeout(this.refreshTimer);
    }

    //#region SFTP Management code
    /* As mentioned in the html file, the SFTP Management stuff was commented out at the time of this conversion however in case it is still
        needed in the future I've preserved it here - Richard Curylo
    */

    //#endregion SFTP Management code
}
